// Rail (узкая иконочная полоса слева), Sidebar (дерево скважин), Topbar
// (breadcrumbs + поиск + actions). Портировано из design_ref/src/Shell.jsx.

import { createSignal, createMemo, For, Show } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import { WELLS, FIELDS, RECENT } from './data'
import type { Well } from './data'
import { useAuth, LOGIN_URL } from './auth.tsx'
import type { Me } from './auth.tsx'

// Инициалы для avatar-плашки, когда нет picture_url. Берём первые буквы
// из name (по словам); если name пуст — первый символ email.
function initials(u: Me): string {
  if (u.name && u.name.trim().length > 0) {
    const parts = u.name.trim().split(/\s+/)
    const first = parts[0]?.[0] ?? ''
    const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
    return (first + second).toUpperCase()
  }
  return u.email.slice(0, 1).toUpperCase()
}

const RAIL_ITEMS = [
  { id: 'dashboard', icon: 'home',     label: 'Главная' },
  { id: 'wells',     icon: 'table',    label: 'Все скважины' },
  { id: 'files',     icon: 'files',    label: 'Файлы' },
  { id: 'telemetry', icon: 'chart',    label: 'Телеметрия' },
  { id: 'tasks',     icon: 'bug',      label: 'Задачи' },
  { id: 'tools',     icon: 'drill',    label: 'Инструмент' },
  { id: 'fields',        icon: 'map',      label: 'Месторождения' },
  { id: 'clusters',      icon: 'map',      label: 'Площадки (кусты)' },
  { id: 'wellbores',     icon: 'well',     label: 'Каталог стволов' },
  { id: 'casings',       icon: 'well',     label: 'Каталог обсадки' },
  { id: 'muds',          icon: 'activity', label: 'Каталог растворов' },
  { id: 'public_states', icon: 'folder',   label: 'Public states' },
  { id: 'states',        icon: 'files',    label: 'Мои стейты' },
  { id: 'knbk_assets',   icon: 'drill',    label: 'Ассеты КНБК' },
] as const

type RailProps = {
  active: string
  onChange: (v: string) => void
  onSearch: () => void
  onSettings: () => void
  onToggleSidebar: () => void
}

export const Rail: Component<RailProps> = (props) => (
  <div class="rail">
    <div class="rail-logo" title="Drilling Journal">DJ</div>

    <For each={RAIL_ITEMS}>
      {(it) => (
        <div
          class={'rail-btn' + (props.active === it.id ? ' active' : '')}
          title={it.label}
          onClick={() => props.onChange(it.id)}
        >
          <Icon name={it.icon} />
        </div>
      )}
    </For>
    <div class="rail-spacer" />
    <div class="rail-btn" title="Поиск (⌘K)" onClick={() => props.onSearch()}><Icon name="search" /></div>
    <div class="rail-btn" title="Скрыть/показать сайдбар (⌘\)" onClick={() => props.onToggleSidebar()}><Icon name="sidebar" /></div>
    <div class="rail-btn" title="Настройки" onClick={() => props.onSettings()}><Icon name="settings" /></div>
  </div>
)

type SidebarProps = {
  activeWell: string
  currentView: string
  onOpenWell: (id: string) => void
  onOpenView: (v: string) => void
  onOpenSettings: () => void
}

export const Sidebar: Component<SidebarProps> = (props) => {
  const { me } = useAuth()
  const [collapsed, setCollapsed] = createSignal({ recent: false, fields: false, tags: true })
  const toggle = (k: 'recent' | 'fields' | 'tags') =>
    setCollapsed((c) => ({ ...c, [k]: !c[k] }))

  return (
    <div class="sidebar">
      <div class="sidebar-header">
        <div class="sidebar-title">
          <Icon name="folder" size={12} />
          Рабочее пространство
        </div>
        <div class="sidebar-actions">
          <button class="btn-icon" title="Создать скважину"><Icon name="plus" /></button>
        </div>
      </div>

      <div class="sidebar-search">
        <div class="input">
          <Icon name="search" />
          <input placeholder="Найти скважину…" />
        </div>
      </div>

      <div class="sidebar-body">
        <div class={'tree-section' + (collapsed().recent ? ' collapsed' : '')}>
          <div class="tree-section-head" onClick={() => toggle('recent')}>
            <Icon name="chevron" size={10} /> Недавние
          </div>
          <div class="tree-list">
            <For each={RECENT.slice(0, 4)}>
              {(r) => (
                <div class="tree-item" onClick={() => props.onOpenView('note')}>
                  <Icon name={r.kind === 'file' ? 'file' : r.kind === 'incident' ? 'bug' : 'notes'} />
                  <span class="tree-label">{r.title}</span>
                  <span class="tree-meta">{r.id.split('-')[0]}</span>
                </div>
              )}
            </For>
          </div>
        </div>

        <div class={'tree-section' + (collapsed().fields ? ' collapsed' : '')}>
          <div class="tree-section-head" onClick={() => toggle('fields')}>
            <Icon name="chevron" size={10} /> Месторождения
          </div>
          <div class="tree-list">
            <For each={FIELDS}>
              {(f) => (
                <>
                  <div class="tree-item">
                    <Icon name="map" />
                    <span class="tree-label">{f.name}</span>
                    <span class="tree-meta">{f.count}</span>
                  </div>
                  <For each={WELLS.filter((w) => w.field === f.name).slice(0, 3)}>
                    {(w: Well) => (
                      <div
                        class={'tree-item nested' + (props.activeWell === w.id && props.currentView === 'well' ? ' active' : '')}
                        onClick={() => props.onOpenWell(w.id)}
                      >
                        <span class={'dot ' + (w.status === 'active' ? 'ok' : w.status === 'warn' ? 'warn' : w.status === 'err' ? 'err' : 'idle')} />
                        <span class="tree-label">{w.name}</span>
                        <span class="tree-meta">{w.id.split('-').slice(-1)[0]}</span>
                      </div>
                    )}
                  </For>
                </>
              )}
            </For>
          </div>
        </div>

        <div class={'tree-section' + (collapsed().tags ? ' collapsed' : '')}>
          <div class="tree-section-head" onClick={() => toggle('tags')}>
            <Icon name="chevron" size={10} /> Теги
          </div>
          <div class="tree-list">
            <For each={['#бурение', '#каротаж', '#осложнения', '#грп', '#окс']}>
              {(t) => (
                <div class="tree-item">
                  <Icon name="tag" />
                  <span class="tree-label mono">{t}</span>
                </div>
              )}
            </For>
          </div>
        </div>
      </div>

      <Show
        when={me() != null}
        fallback={
          <div class="sidebar-footer">
            <a
              class="btn"
              href={LOGIN_URL}
              style={{ flex: 1, 'justify-content': 'center', 'text-decoration': 'none' }}
            >
              <Icon name="user" />
              Войти через Google
            </a>
          </div>
        }
      >
        {(_) => {
          // Show рендерит блок только когда me() — Me, не null/undefined.
          const u = () => me() as Me
          return (
            <div
              class="sidebar-footer"
              style={{ cursor: 'pointer' }}
              title="Открыть настройки аккаунта"
              onClick={() => props.onOpenSettings()}
            >
              <Show
                when={u().picture_url}
                fallback={<div class="avatar">{initials(u())}</div>}
              >
                <img
                  class="avatar"
                  src={u().picture_url!}
                  alt=""
                  referrerpolicy="no-referrer"
                  style={{ 'object-fit': 'cover' }}
                />
              </Show>
              <div style={{ flex: 1, 'min-width': 0 }}>
                <div
                  style={{
                    'font-size': 'var(--fs-sm)',
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    'text-overflow': 'ellipsis',
                    'white-space': 'nowrap',
                  }}
                >
                  {u().name ?? u().email}
                </div>
                <div
                  style={{
                    'font-size': '10px',
                    overflow: 'hidden',
                    'text-overflow': 'ellipsis',
                    'white-space': 'nowrap',
                  }}
                >
                  {u().email}
                </div>
              </div>
              <span class={'dot ' + (u().is_active ? 'ok' : 'idle')} title={u().is_active ? 'активен' : 'отключён'} />
            </div>
          )
        }}
      </Show>
    </div>
  )
}

type Crumb = { label: string; current?: boolean; view?: string }

type TopbarProps = {
  view: string
  well: Well | undefined
  onOpenView: (v: string) => void
  onOpenSearch: () => void
  onToggleMobileMenu: () => void
}

export const Topbar: Component<TopbarProps> = (props) => {
  const crumbs = createMemo<Crumb[]>(() => {
    const v = props.view
    const w = props.well
    if (v === 'dashboard') return [{ label: 'Главная', current: true }]
    if (v === 'wells')     return [{ label: 'Все скважины', current: true }]
    if (v === 'files')     return [{ label: 'Файлы', current: true }]
    if (v === 'telemetry') return [{ label: 'Телеметрия', current: true }]
    if (v === 'tasks')     return [{ label: 'Задачи', current: true }]
    if (v === 'tools')     return [{ label: 'Инструмент', current: true }]
    if (v === 'fields')        return [{ label: 'Месторождения', current: true }]
    if (v === 'clusters')      return [{ label: 'Площадки (кусты)', current: true }]
    if (v === 'wellbores')     return [{ label: 'Каталог стволов', current: true }]
    if (v === 'casings')       return [{ label: 'Каталог обсадки', current: true }]
    if (v === 'muds')          return [{ label: 'Каталог растворов', current: true }]
    if (v === 'public_states') return [{ label: 'Public states', current: true }]
    if (v === 'knbk_assets')   return [{ label: 'Ассеты КНБК', current: true }]
    if (v === 'settings')      return [{ label: 'Настройки', current: true }]
    if (v === 'well' && w) return [
      { label: w.field, view: 'wells' },
      { label: w.name, current: true },
    ]
    if (v === 'note' && w) return [
      { label: w.field, view: 'wells' },
      { label: w.name, view: 'well' },
      { label: 'Журнал — 28.04.2026', current: true },
    ]
    return []
  })

  return (
    <div class="topbar">
      <div class="topbar-left">
        <button
          class="btn-icon mobile-menu-btn"
          title="Меню"
          aria-label="Открыть меню"
          onClick={() => props.onToggleMobileMenu()}
        >
          <Icon name="menu" />
        </button>
        <div class="crumbs">
          <For each={crumbs()}>
            {(c, i) => (
              <>
                <span
                  class={'crumb' + (c.current ? ' current' : '')}
                  onClick={() => c.view && props.onOpenView(c.view)}
                >
                  {c.label}
                </span>
                <Show when={i() < crumbs().length - 1}>
                  <span class="sep">/</span>
                </Show>
              </>
            )}
          </For>
        </div>
      </div>
      <div class="topbar-center">
        <button
          class="input global-search"
          onClick={() => props.onOpenSearch()}
          style={{ cursor: 'pointer', color: 'var(--text-tertiary)' }}
        >
          <Icon name="search" />
          <span style={{ flex: 1, 'text-align': 'left' }}>Поиск по базе…</span>
          <span class="kbd">⌘K</span>
        </button>
      </div>
      <div class="topbar-right">
        <button class="btn btn-ghost" title="Загрузить файл"><Icon name="upload" />Загрузить</button>
        <button class="btn btn-primary"><Icon name="plus" />Создать</button>
      </div>
    </div>
  )
}
